import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Server, LogOut, Loader2, Layers, RefreshCw, Menu, ChevronDown, AlertTriangle, Ticket } from 'lucide-react';
import JobManagement from './JobManagement';
import Network40GDashboard from './Network40GDashboard';
import NodeMap from './NodeMap';
import TerminalDashboard from './components/TerminalDashboard';
import CaseCheck from './CaseCheck';
import Docs from './Docs';
import Submit from './Submit';
import Desktop from './Desktop';
import useIsMobile from './useIsMobile';
import { useTopology } from './TopologyContext';
import { useData } from './DataContext';
import { getNodeData } from './components/primitives';
import { HeaderClock } from './components/HeaderClock';
import { NodeCard } from './components/NodeCard';
import NotificationWatcher from './components/NotificationWatcher';
import ErrorBoundary from './components/ErrorBoundary';
import {
  ClusterSummaryCard, MobileSummaryCard,
  TotalResourcesCard, MiniTotalCard,
} from './components/SummaryCards';
import {
  RunningJobsCard, PendingJobsCard, CompletedJobsCard,
} from './components/JobCards';
import {
  ClusterNodeSection, UnassignedNodeSection,
} from './components/NodeSections';

const TABS = [
  { id: 'dashboard',  label: 'Dashboard' },
  { id: 'jobs',       label: 'Jobs' },
  { id: 'submit',     label: 'Submit' },
  { id: 'docs',       label: 'Docs' },
  { id: 'casecheck',  label: 'Case Check' },
  { id: 'network40g', label: '40G Network' },
  { id: 'nodemap',    label: 'Node Map' },
  { id: 'terminal',   label: 'Terminal' },
  { id: 'desktop',    label: 'Desktop' },
];

function StaleQueueBanner({ clusters, onCleared }) {
  const [clearing, setClearing] = useState(null);
  const stale = Object.entries(clusters || {}).filter(([, c]) => c && c.staleHead);
  if (stale.length === 0) return null;
  const clear = async (cid) => {
    setClearing(cid);
    try {
      await axios.post(`/api/clusters/${cid}/clear-stale-head`);
      if (onCleared) onCleared();
    } catch { /* surfaced on next poll */ }
    finally { setClearing(null); }
  };
  return (
    <div style={{ marginBottom: 16 }}>
      {stale.map(([cid, c]) => (
        <div key={cid} style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: '10px 14px', marginBottom: 8, borderRadius: 8,
          background: 'rgba(250,204,21,0.08)', border: '1px solid rgba(250,204,21,0.35)',
        }}>
          <AlertTriangle style={{ width: 16, height: 16, color: '#facc15', flexShrink: 0 }} />
          <span style={{ color: '#fde68a', fontSize: 13 }}>
            <b>Cluster {cid}: stale queue head.</b>{' '}
            <code style={{ fontSize: 11 }}>{c.staleHead.jobName}</code> is marked RUNNING but isn't running —
            {' '}{c.staleHead.blocked} job{c.staleHead.blocked !== 1 ? 's' : ''} blocked behind it.
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={() => clear(cid)} disabled={clearing === cid} style={{
            padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, background: '#f59e0b', color: '#1a1200',
          }}>{clearing === cid ? 'Clearing…' : 'Clear stale entry'}</button>
        </div>
      ))}
    </div>
  );
}

function NodeStatus({ user, onLogout }) {
  const { ALL_NODES, NODE_COUNT, CLUSTERS, NODE_TO_CLUSTER } = useTopology();
  const {
    resources, clusters, network40g, nodeUsage, license, jobsLive,
    loading, isRefreshing, refresh, ts,
  } = useData();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [openJob, setOpenJob] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const isMobile = useIsMobile();

  const openJobInTab = (j) => { setOpenJob(j); setActiveTab('jobs'); };

  // Deep link: ?job=<opaque id> (from a completion email) → open that job in the
  // Jobs tab. The id reveals nothing; we resolve it to a job after login.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jid = params.get('job');
    if (!jid) return;
    window.history.replaceState({}, '', window.location.pathname);
    let cancelled = false;
    (async () => {
      try {
        const hist = await axios.get('/api/jobs/history').catch(() => null);
        let rec = hist?.data?.jobs?.find((r) => r.id === jid);
        if (rec) { if (!cancelled) openJobInTab({ ...rec, fromArchive: true }); return; }
        const run = await axios.get('/api/jobs/running').catch(() => null);
        rec = (run?.data?.jobs || []).find((r) => r.id === jid);
        if (rec && !cancelled) openJobInTab(rec);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cluster-aware job aggregates. Each job carries a `cluster` field so card
  // subsections can split them.
  const jobs = useMemo(() => {
    const running = [], pending = [], completed = [];
    Object.entries(clusters).forEach(([cid, cdata]) => {
      (cdata.running || []).forEach(j => running.push({ ...j, cluster: cid }));
      (cdata.pending || []).forEach(j => pending.push({ ...j, cluster: cid }));
      (cdata.completed || []).forEach(j => completed.push({ ...j, cluster: cid }));
    });
    return { running, pending, completed };
  }, [clusters]);

  // Per-cluster counts surface to NodeCards.
  const clusterCounts = useMemo(() => {
    const map = {};
    Object.keys(CLUSTERS).forEach(cid => {
      const c = clusters[cid];
      map[cid] = {
        running: c?.running?.length || 0,
        pending: c?.pending?.length || 0,
      };
    });
    return map;
  }, [clusters, CLUSTERS]);

  if (loading) return (
    <div style={{
      minHeight: '100vh', backgroundColor: '#111827',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ textAlign: 'center' }}>
        <Loader2 style={{
          width: 48, height: 48, color: '#3b82f6',
          animation: 'spin 1s linear infinite', margin: '0 auto 16px',
        }} />
        <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 8 }}>Loading...</h2>
      </div>
    </div>
  );

  const onlineCount = getNodeData(resources, ALL_NODES).filter(r => !r.error).length;

  const renderDesktopDashboard = () => (
    <>
      {/* Row 1: Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, marginBottom: 24 }}>
        <ClusterSummaryCard
          resources={resources}
          totalJobs={{ running: jobs.running.length, pending: jobs.pending.length }}
        />
        <RunningJobsCard jobs={jobs.running} jobsLive={jobsLive} isMobile={false} onOpenJob={openJobInTab} />
        <PendingJobsCard jobs={jobs.pending} isMobile={false} />
        <CompletedJobsCard jobs={jobs.completed} isMobile={false} />
      </div>

      {/* Row 1b: License (SRU) capacity */}
      <LicenseCard license={license} />

      {/* Row 2: Section header */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Server style={{ width: 18, height: 18, color: '#3b82f6' }} />
          <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 600, margin: 0 }}>
            Compute Nodes
          </h2>
          <span style={{ color: '#6b7280', fontSize: 13 }}>
            ({onlineCount}/{NODE_COUNT} online)
          </span>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          fontSize: 11, color: '#6b7280',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22c55e' }} />
            Online
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444' }} />
            Offline
          </span>
        </div>
      </div>

      {/* Row 3: Node grid grouped by cluster */}
      {Object.entries(CLUSTERS).map(([cid, c]) => (
        <ClusterNodeSection
          key={cid}
          clusterId={cid}
          cluster={c}
          counts={clusterCounts[cid]}
          resources={resources}
          network40g={network40g}
          nodeUsage={nodeUsage}
        />
      ))}
      {(() => {
        const unassigned = ALL_NODES.filter(n => !NODE_TO_CLUSTER[n]);
        if (unassigned.length === 0) return null;
        return (
          <UnassignedNodeSection
            nodes={unassigned}
            resources={resources}
            network40g={network40g}
            nodeUsage={nodeUsage}
          />
        );
      })()}

      {/* Row 4: Banner */}
      <TotalResourcesCard resources={resources} />
    </>
  );

  const renderMobileDashboard = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <MobileSummaryCard resources={resources} jobs={jobs} />
      <LicenseCard license={license} />
      <RunningJobsCard jobs={jobs.running} jobsLive={jobsLive} isMobile={true} onOpenJob={openJobInTab} />
      <PendingJobsCard jobs={jobs.pending} isMobile={true} />
      <CompletedJobsCard jobs={jobs.completed} isMobile={true} />

      <div style={{ marginTop: 8 }}>
        <h2 style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
          Compute Nodes ({onlineCount}/{NODE_COUNT})
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {ALL_NODES.map(node => (
            <NodeCard
              key={node}
              node={node}
              data={resources.find(r => r.node === node) || {}}
              network40g={network40g}
              nodeUsage={nodeUsage}
              clusterId={NODE_TO_CLUSTER[node]}
              clusterCount={clusterCounts[NODE_TO_CLUSTER[node]]}
              isMobile={true}
            />
          ))}
          <MiniTotalCard resources={resources} />
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#111827',
    }}>
        <NotificationWatcher />
        {/* Header */}
        <header className="no-print" style={{
          padding: isMobile ? 16 : '20px 32px',
          borderBottom: '1px solid #1f2937',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', flexWrap: 'wrap', gap: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Layers style={{ width: 18, height: 18, color: '#fff' }} />
              </div>
              <div>
                <h1 style={{
                  fontSize: isMobile ? 14 : 18, fontWeight: 'bold',
                  color: '#fff', margin: 0,
                }}>SYBERJET HPC</h1>
                {!isMobile && (
                  <p style={{ color: '#6b7280', fontSize: 12, margin: 0 }}>
                    PowerFlow Compute Dashboard — {NODE_COUNT} Nodes
                  </p>
                )}
              </div>
            </div>

            {!isMobile && (
              <div style={{ textAlign: 'right' }}>
                <p style={{ color: '#fff', fontWeight: 500, fontSize: 14, margin: 0 }}>
                  Welcome back, {user}
                </p>
                <p style={{ color: '#6b7280', fontSize: 12, margin: 0 }}>
                  {jobs.running.length} simulation{jobs.running.length !== 1 ? 's' : ''} running
                </p>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {!isMobile && <HeaderClock />}
              <LastUpdated ts={ts} />
              <button
                onClick={refresh}
                style={{
                  padding: 8, borderRadius: 10, border: 'none',
                  backgroundColor: '#1f2937', cursor: 'pointer',
                }}
                title="Refresh"
              >
                <RefreshCw style={{
                  width: 16, height: 16, color: '#9ca3af',
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                }} />
              </button>
              <button
                onClick={onLogout}
                style={{
                  padding: 8, borderRadius: 10, border: 'none',
                  backgroundColor: 'rgba(239,68,68,0.2)', cursor: 'pointer',
                }}
                title="Logout"
              >
                <LogOut style={{ width: 16, height: 16, color: '#ef4444' }} />
              </button>
            </div>
          </div>
        </header>

        {/* Tabs: hamburger dropdown on mobile, horizontal strip on desktop */}
        {isMobile ? (
          <nav className="no-print" style={{ backgroundColor: '#0d1424', borderBottom: '1px solid #1f2937', padding: '8px 12px', position: 'relative', zIndex: 40 }}>
            <button onClick={() => setNavOpen(o => !o)} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
              background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: '11px 14px',
              color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Menu style={{ width: 17, height: 17, color: '#3b82f6' }} />
                {TABS.find(t => t.id === activeTab)?.label || 'Menu'}
              </span>
              <ChevronDown style={{ width: 16, height: 16, color: '#9ca3af', transform: navOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            {navOpen && (
              <div style={{
                position: 'absolute', left: 12, right: 12, top: '100%', marginTop: 4, zIndex: 50,
                background: '#0d1424', border: '1px solid #1f2937', borderRadius: 8, overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(0,0,0,0.55)',
              }}>
                {TABS.map(tab => {
                  const active = activeTab === tab.id;
                  return (
                    <button key={tab.id} onClick={() => { setActiveTab(tab.id); setNavOpen(false); }} style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '13px 16px', border: 'none',
                      background: active ? 'rgba(59,130,246,0.15)' : 'transparent',
                      color: active ? '#fff' : '#cbd5e1', fontSize: 15, fontWeight: active ? 600 : 500,
                      cursor: 'pointer', borderLeft: active ? '3px solid #3b82f6' : '3px solid transparent',
                    }}>{tab.label}</button>
                  );
                })}
              </div>
            )}
          </nav>
        ) : (
          <nav className="no-print" style={{
            padding: '0 32px', borderBottom: '1px solid #1f2937', backgroundColor: '#0d1424',
            display: 'flex', gap: 4, overflowX: 'auto',
          }}>
            {TABS.map(tab => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '12px 20px', border: 'none',
                    borderBottom: active ? '2px solid #3b82f6' : '2px solid transparent',
                    marginBottom: -1, background: 'transparent', cursor: 'pointer',
                    fontSize: 14, fontWeight: active ? 600 : 500,
                    color: active ? '#fff' : '#9ca3af',
                    transition: 'color 0.15s, border-color 0.15s', whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.color = '#d1d5db'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.color = '#9ca3af'; }}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        )}

        {/* Main */}
        <main style={{ padding: isMobile ? 16 : 32 }}>
          <StaleQueueBanner clusters={clusters} onCleared={refresh} />
          <ErrorBoundary key={activeTab} label={activeTab}>
            {activeTab === 'dashboard' && (isMobile ? renderMobileDashboard() : renderDesktopDashboard())}
            {activeTab === 'jobs' && <JobManagement user={user} openJob={openJob} onOpenConsumed={() => setOpenJob(null)} />}
            {activeTab === 'submit' && <Submit />}
            {activeTab === 'casecheck' && <CaseCheck />}
            {activeTab === 'docs' && <Docs />}
            {activeTab === 'network40g' && <Network40GDashboard />}
            {activeTab === 'nodemap' && (
              <NodeMap resources={resources} network40g={network40g} nodeUsage={nodeUsage} clusterCounts={clusterCounts} />
            )}
            {activeTab === 'terminal' && <TerminalDashboard />}
            {activeTab === 'desktop' && <Desktop />}
          </ErrorBoundary>
      </main>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Live data-freshness indicator. DataContext keeps the last-good snapshot on a
// failed poll (so the dashboard never blanks), but that means it can silently
// show stale numbers during a network blip — this makes staleness visible.
function LastUpdated({ ts }) {
  const [now, setNow] = React.useState(() => Math.floor(Date.now() / 1000));
  React.useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 2000);
    return () => clearInterval(id);
  }, []);
  if (!ts) return null;
  const age = Math.max(0, now - ts);
  const stale = age > 20;   // snapshot polls every 5s; >20s means we're missing updates
  const color = stale ? '#f59e0b' : '#22c55e';
  const label = stale ? (age >= 60 ? `stale ${Math.floor(age / 60)}m` : `stale ${age}s`) : 'live';
  return (
    <span title={stale ? 'Dashboard is not receiving fresh data — showing last-known values.' : 'Live data'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color, whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color,
        animation: stale ? 'none' : 'pulse 2s infinite' }} />
      {label}
    </span>
  );
}

// PowerFLOW SRU license capacity — live "used vs pool" + how many more jobs can
// start. Used is summed from running jobs; the pool total is a backend config
// value (DSLS server total isn't queryable from the compute nodes).
function LicenseCard({ license }) {
  const total = license?.sruTotal ?? 0;
  if (!total) return null;
  const used = license?.sruUsed ?? 0;
  const free = license?.sruFree ?? Math.max(0, total - used);
  const perJob = license?.sruPerJob || 150;
  const running = license?.jobsRunning ?? 0;
  const maxc = license?.maxConcurrent ?? Math.floor(total / perJob);
  const moreJobs = Math.floor(free / perJob);
  const pct = Math.min(100, (used / total) * 100);
  const color = pct >= 95 ? '#ef4444' : pct >= 75 ? '#f59e0b' : '#22c55e';
  return (
    <div style={{
      backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: 16,
      padding: '14px 20px', marginBottom: 24,
      display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 200 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 9, backgroundColor: `${color}22`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Ticket style={{ width: 18, height: 18, color }} />
        </div>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            PowerFLOW Licenses
          </div>
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>
            {used} <span style={{ color: '#64748b', fontWeight: 400 }}>/ {total} SRU</span>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ height: 8, backgroundColor: '#1f2937', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${pct}%`, backgroundColor: color,
            borderRadius: 4, transition: 'width 0.5s ease-out',
          }} />
        </div>
        <div style={{ color: '#64748b', fontSize: 11, marginTop: 5 }}>
          {free} SRU free · {perJob} SRU per job
        </div>
      </div>

      <div style={{ display: 'flex', gap: 22 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>{running}<span style={{ color: '#64748b', fontSize: 13, fontWeight: 400 }}>/{maxc}</span></div>
          <div style={{ color: '#94a3b8', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Jobs running</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: moreJobs > 0 ? '#22c55e' : '#f59e0b', fontSize: 18, fontWeight: 700 }}>
            {moreJobs > 0 ? `+${moreJobs}` : 'Full'}
          </div>
          <div style={{ color: '#94a3b8', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {moreJobs > 0 ? 'Can start' : 'License-capped'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NodeStatus;
